// Deterministic, human-friendly pseudonym for a visitor, derived purely from
// their localStorage UUID. The same UUID always yields the same name — across
// sessions, browsers, and the agent/admin apps — with no randomness, no
// persistence, and no dependencies. Lets the monitor show
// "SwiftAmberTiger·4F2A" instead of a raw UUID.
//
// ── Word banks ──
// adjective + modifier + animal → e.g. "Swift" + "Amber" + "Tiger".
// 50 each → 125,000 word triples; the 4-char suffix disambiguates collisions.
// Keep entries short — the display target is ~24 chars; the longest possible
// combination here ("StellarSaffronTortoise·····") runs to 27, so the table
// cell should tolerate mild overflow.
const ADJECTIVES: ReadonlyArray<string> = [
  'Swift', 'Calm', 'Bright', 'Bold', 'Quiet', 'Eager', 'Gentle', 'Clever', 'Lucky', 'Brave',
  'Sunny', 'Witty', 'Nimble', 'Jolly', 'Merry', 'Cosmic', 'Royal', 'Noble', 'Mighty', 'Daring',
  'Steady', 'Lively', 'Curious', 'Dapper', 'Breezy', 'Cheery', 'Plucky', 'Spry', 'Zesty', 'Snappy',
  'Glossy', 'Tidy', 'Cozy', 'Sleek', 'Pure', 'Vivid', 'Wise', 'Sharp', 'Keen', 'Crisp',
  'Polar', 'Solar', 'Lunar', 'Stellar', 'Velvet', 'Mellow', 'Hearty', 'Stout', 'Tame', 'Loyal',
];
const MODIFIERS: ReadonlyArray<string> = [
  'Amber', 'Coral', 'Indigo', 'Crimson', 'Jade', 'Onyx', 'Pearl', 'Topaz', 'Ruby', 'Ivory',
  'Saffron', 'Cobalt', 'Copper', 'Bronze', 'Silver', 'Golden', 'Marble', 'Velvet', 'Silk', 'Linen',
  'Maple', 'Cedar', 'Birch', 'Willow', 'Aspen', 'Mossy', 'Misty', 'Cloudy', 'Frosty', 'Stormy',
  'Sandy', 'Pebble', 'Granite', 'Slate', 'Chalk', 'Cinder', 'Ember', 'Smoky', 'Dusty', 'Hazy',
  'Plum', 'Olive', 'Mint', 'Lemon', 'Peach', 'Cherry', 'Hazel', 'Russet', 'Auburn', 'Cobweb',
];
const ANIMALS: ReadonlyArray<string> = [
  'Tiger', 'Falcon', 'Panda', 'Otter', 'Heron', 'Cobra', 'Lynx', 'Bison', 'Eagle', 'Wolf',
  'Fox', 'Hawk', 'Crane', 'Robin', 'Sparrow', 'Owl', 'Lion', 'Bear', 'Stag', 'Deer',
  'Mantis', 'Beetle', 'Cricket', 'Dolphin', 'Seal', 'Whale', 'Trout', 'Marlin', 'Koi', 'Crab',
  'Gecko', 'Iguana', 'Tortoise', 'Toad', 'Newt', 'Mole', 'Badger', 'Beaver', 'Ferret', 'Marten',
  'Hare', 'Ibex', 'Yak', 'Camel', 'Horse', 'Bull', 'Goat', 'Ram', 'Swan', 'Goose',
];

// Crockford base32 — omits I, L, O, U to avoid look-alike confusion. 32 chars,
// so each character carries 5 bits. Used to render the readable 4-char suffix.
const SUFFIX_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const SUFFIX_LENGTH = 4;

// FNV-1a (32-bit). Fast, inline, dependency-free. `Math.imul` keeps the
// multiply in 32-bit space; `>>> 0` returns an unsigned result so the modulo
// below is never negative.
function fnv1a(input: string): number {
  let h = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193); // FNV prime
  }
  return h >>> 0;
}

// Pick from a list by hashing the UUID with a per-category salt, so the three
// words vary independently rather than moving in lockstep.
function pick(list: ReadonlyArray<string>, uuid: string, salt: string): string {
  return list[fnv1a(uuid + salt) % list.length]!;
}

// 4 readable characters derived from a salted hash of the UUID (NOT a
// timestamp): each char consumes 5 bits of the 32-bit hash.
function suffixFor(uuid: string): string {
  let h = fnv1a(uuid + '|sfx');
  let out = '';
  for (let i = 0; i < SUFFIX_LENGTH; i++) {
    out += SUFFIX_ALPHABET[h & 31];
    h >>>= 5;
  }
  return out;
}

export interface Pseudonym {
  /** Full display string, e.g. "SwiftAmberTiger·4F2A". */
  display: string;
  adjective: string;
  modifier: string;
  animal: string;
  /** 4-char readable uniqueness suffix derived from the UUID. */
  suffix: string;
}

/**
 * Deterministic pseudonym for a visitor UUID. Returns the display string and
 * its parts. Input is lower-cased first so the (case-insensitive) UUID maps to
 * the same name regardless of casing.
 */
export function pseudonymForUuid(uuid: string): Pseudonym {
  const id = uuid.toLowerCase();
  const adjective = pick(ADJECTIVES, id, '|adj');
  const modifier = pick(MODIFIERS, id, '|mod');
  const animal = pick(ANIMALS, id, '|ani');
  const suffix = suffixFor(id);
  return {
    display: `${adjective}${modifier}${animal}·${suffix}`,
    adjective,
    modifier,
    animal,
    suffix,
  };
}
